<template>
    <v-card class="project-alarm rounded-xl card-bg card-bd h-100 d-flex flex-column" variant="elevated" elevation="0">
        <template #title>
            <div class="d-flex justify-space-between align-center">
                <span>项目告警统计</span>
            </div>
        </template>
        <div class="d-flex flex-column flex-grow-1 overflow-hidden px-6 pb-4">
            <!-- 月份选择器 -->
            <div class="month-selector d-flex align-center ga-2 mb-4">
                <v-btn icon="mdi-chevron-left" size="small" variant="text" @click="prevMonth"></v-btn>
                <span class="month-text">{{ formatMonth(currentDate) }}</span>
                <v-btn icon="mdi-chevron-right" size="small" variant="text" @click="nextMonth"></v-btn>
            </div>

            <!-- 图表区域 -->
            <div class="chart-wrapper flex-grow-1 position-relative">
                <template v-if="hasData">
                    <div class="chart-container">
                        <!-- Y轴 -->
                        <div class="chart-y-axis">
                            <div v-for="value in yAxisList" :key="value" class="y-axis-item">
                                <span class="y-axis-text">{{ value }}</span>
                                <span class="y-axis-line"></span>
                            </div>
                        </div>

                        <!-- 柱状图 -->
                        <div class="chart-bars">
                            <div class="bars-wrapper">
                                <div v-for="(item, index) in list" :key="item.stationId || index" class="bar-item">
                                    <v-tooltip location="top">
                                        <template v-slot:activator="{ props }">
                                            <div v-bind="props" class="bar-column"
                                                :style="{ '--height': `${(item.count / maxY) * 100}%` }">
                                                <span class="bar-value">{{ item.count }}</span>
                                            </div>
                                        </template>
                                        <div>
                                            <div>电站: {{ item.stationName || '--' }}</div>
                                            <div>告警数: <span class="text-success">{{ item.count }}</span></div>
                                        </div>
                                    </v-tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <div v-else class="empty-state d-flex align-center justify-center h-100">
                    <span class="text-grey">暂无数据</span>
                </div>
            </div>
        </div>
    </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getAlarmStationCount } from '@/api/alarm'
import dayjs from 'dayjs'

const currentDate = ref(new Date())
const list = ref([])

const hasData = computed(() => list.value.length > 0)

const maxValue = computed(() => {
    return Math.max(...list.value.map(item => item.count), 0)
})

const yAxisList = computed(() => {
    return getAxisList(maxValue.value)
})

const maxY = computed(() => {
    return yAxisList.value[0] || 1
})

function formatMonth(date) {
    return dayjs(date).format('YYYY年MM月')
}

function prevMonth() {
    currentDate.value = dayjs(currentDate.value).subtract(1, 'month').toDate()
    fetchData()
}

function nextMonth() {
    currentDate.value = dayjs(currentDate.value).add(1, 'month').toDate()
    fetchData()
}

function getAxisList(maxNum) {
    if (!maxNum || maxNum === 0) return [100, 80, 60, 40, 20, 0]

    const axisList = []
    const splitNumber = 5
    let step = Math.ceil(maxNum / splitNumber)
    let stepLength = String(step).length - 1
    let newStep = Math.ceil(step / 10 ** stepLength) * (10 ** stepLength)
    for (let i = 0; i <= splitNumber; i++) {
        axisList.unshift(i * newStep)
    }
    return axisList
}

async function fetchData() {
    try {
        const res = await getAlarmStationCount({
            startTime: dayjs(currentDate.value).startOf('month').format('YYYY-MM-DD HH:mm:ss'),
            endTime: dayjs(currentDate.value).endOf('month').format('YYYY-MM-DD HH:mm:ss'),
        })
        if (res.code === 200 && res.data) {
            list.value = res.data
        }
    } catch (error) {
        console.error('Failed to fetch alarm station count:', error)
    }
}

onMounted(() => {
    fetchData()
})
</script>

<style scoped lang="scss">
.project-alarm {
    :deep(.v-card-item) {
        padding: 24px 28px 10px 28px !important;

        .v-card-title {
            font-weight: 400 !important;
            font-size: 20px !important;
        }
    }
}

.card-bg {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
}

.card-bd {
    border: 1px solid #fff;
}

.month-selector {
    justify-content: center;
}

.month-text {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    min-width: 100px;
    text-align: center;
}

.chart-wrapper {
    overflow: hidden;
}

.chart-container {
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
}

.chart-y-axis {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.y-axis-item {
    display: flex;
    align-items: center;
}

.y-axis-text {
    color: #9CA3AF;
    font-size: 12px;
    min-width: 40px;
    text-align: right;
    padding-right: 8px;
}

.y-axis-line {
    flex: 1;
    height: 1px;
    background: #E5E7EB;
}

.chart-bars {
    position: absolute;
    inset: 0;
    left: 48px;
    overflow-x: auto;
    overflow-y: hidden;
}

.bars-wrapper {
    height: 100%;
    display: flex;
    gap: 24px;
    padding: 16px 24px;
    align-items: flex-end;
}

.bar-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    min-width: 20px;
    height: 100%;
}

.bar-column {
    width: 16px;
    height: var(--height);
    min-height: 4px;
    background: linear-gradient(180deg, #60A5FA 0%, #3B82F6 100%);
    border-radius: 4px 4px 0 0;
    position: relative;
    cursor: pointer;
    transition: transform 0.3s ease;
    animation: barGrow 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;

    &:hover {
        transform: scaleY(1.05);
        filter: brightness(1.1);
        box-shadow: 0 0 15px rgba(59, 130, 246, 0.6);
    }
}

@keyframes barGrow {
    from {
        height: 0;
        opacity: 0;
    }

    to {
        height: var(--height);
        opacity: 1;
    }
}

.bar-value {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 11px;
    color: #6B7280;
    white-space: nowrap;
}

.empty-state {
    color: #9CA3AF;
}
</style>
